import { queryClient, useApiClient } from "@/infrastructure/rest";
import { DossierSettingsEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { DetermineDossierSettingsDto, DossierSettingsDto, PutDossierSettingDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DOSSIER_SETTINGS_QUERY_KEY } from "./constants";
import { DossierSettings } from "./domain";

function getDossierSettingsQuery(dossierId: string, apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [DOSSIER_SETTINGS_QUERY_KEY, dossierId],
        queryFn: async () => {
            const data = await apiClient.get<DossierSettingsDto>(`/dossiers/${dossierId}/settings`);
            return data ? DossierSettings.fromDto(data) : { enabledSections: [] };
        },
    };
}

export function usePrefetchDossierSettingsQuery(dossierId: string) {
    const apiClient = useApiClient();
    const query = getDossierSettingsQuery(dossierId, apiClient);
    return () => queryClient.prefetchQuery({ ...query, staleTime: 60_000 });
}

export function useGetDossierSettings(dossierId: string) {
    const apiClient = useApiClient();
    const query = getDossierSettingsQuery(dossierId, apiClient);
    return useQuery(query);
}

type DetermineDossierSettingsQuery = DetermineDossierSettingsDto & {
    dossierId: string;
};
export function useDetermineDossierSettings(params: DetermineDossierSettingsQuery) {
    const apiClient = useApiClient();
    const { dossierId, omschrijving, reikwijdte } = params;

    return useQuery({
        queryKey: [DOSSIER_SETTINGS_QUERY_KEY, dossierId, omschrijving, reikwijdte],
        queryFn: async () => {
            const data = await apiClient.post<DossierSettingsDto>(`/dossiers/${dossierId}/settings/secties`, {
                omschrijving,
                reikwijdte,
            });
            return data ? DossierSettings.fromDto(data) : null;
        },
        enabled: Boolean(omschrijving && reikwijdte),
    });
}

type PutDossierSettings = PutDossierSettingDto & {
    dossierId: string;
};

export function usePutDossierSettings() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...payload }: PutDossierSettings) => {
            await apiClient.put(`/dossiers/${dossierId}/settings`, payload);
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIER_SETTINGS_QUERY_KEY, variables.dossierId],
            });
        },
        onError: (error) => {
            console.error("Error puting dossier settings:", error);
        },
    });
}

export function useRealtimeDossierSettings() {
    const queryClient = useQueryClient();
    useRealtimeTopic<DossierSettingsEvent>("dossierSettings", {
        onMessage: (message) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIER_SETTINGS_QUERY_KEY, String(message.dossierId)],
            });
        },
    });
}
